import React from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { useSearch } from '../context/SearchContext';

// Card rendering helpers (copied from SearchResults.jsx)
const filterHtml = html => {
  const processedHtml = html
    // Find ^words and change them to WORDS
    .replace(/\^(\w+)/g, '<span class="acaps">$1</span>')
    // Remove all other special formatting signals
    .replaceAll('↵', '')
    .replaceAll('⇆', '')
    .replaceAll('→→', '<span class="double-indent"></span>')
    .replaceAll('→', '<span class="indent"></span>');
    //.replaceAll('𝄞', ''); @todo: handle poetry formatting

    return processedHtml;
  };

/**
 * Returns space-separated class names for the paragraph element.
 * @param {Object} paragraph - The paragraph object.
 * @returns {string} A string of space-separated class names.
 */
const getParagraphTextClass = paragraph => {
  let classes = [];

  // Classes for the basic type of paragraph this is
  const isPaperTitle = paragraph.psp.endsWith(':0.0');
  const isSectionTitle = /:([1-9]|1[0-9])\.0$/.test(paragraph.psp);
  if (isPaperTitle) {
    classes.push('ub-text-paper-title');
  } else if (isSectionTitle) {
    classes.push('ub-text-section-title');
  } else {
    // Normal paragraph
    classes.push('ub-text-body');
  }

  // Paragraph gaps
  if (paragraph.html.includes('↵↵↵' )) {
    classes.push('extra-large-gap');
  } else if (paragraph.html.includes('↵↵' )) {
    classes.push('large-gap');
  } else if (paragraph.html.includes('↵' )) {
    classes.push('small-gap');
  }

  // Centering
  if (paragraph.html.includes('⇆')) {
    classes.push('text-center');
  }

  return classes.join(' ');
};

/**
 * Formats the paragraph text.
 * @param {Object} paragraph - The paragraph object.
 * @returns {string} The formatted paragraph text.
 */
const formatParagraphText = (paragraph) => {
  const isPaperTitle = paragraph.psp.endsWith(':0.0');
  const isSectionTitle = /:([1-9]|1[0-9])\.0$/.test(paragraph.psp);
  const isCentered = paragraph.html.includes('⇆');
  
  if (isPaperTitle) {
    const paperNum = paragraph.psp.split(':')[0];
    return paperNum > 0 
      ? `Paper ${paperNum}<br>${filterHtml(paragraph.html)}`
      : `${filterHtml(paragraph.html)}`;

  } else if (isSectionTitle) {
    const paperSection = paragraph.psp.split('.')[0];
    return `${filterHtml(paragraph.html)}`;
  }

  // Normal paragraph
  if (isCentered) {
    return `${filterHtml(paragraph.html)}`;
  } else {
    return `<span class="srn-prefix">(${paragraph.psp})</span> ${filterHtml(paragraph.html)}`;
  }
};

/**
 * Determines the appropriate HTML element type based on paragraph content.
 * @param {Object} paragraph - The paragraph object.
 * @returns {string} The HTML element type to render.
 */
const getParagraphElement = paragraph => {
  if (paragraph.psp.endsWith('0.0')) {
    return 'h2';
  } else if (paragraph.psp.endsWith('.0')) {
    return 'h3';
  } else {
    return 'p';
  }
};

/**
 * Converts paragraph.psp to an ID anchor format
 * @param {string} psp - The paragraph psp value (e.g. "1:2.3" or "195:8.1")
 * @returns {string} Formatted ID (e.g. "U1_2_3" or "U195_8_1")
 */
const formatParagraphId = psp => {
  return `U${psp.replace(/[:\.]/g, '_')}`;
};

/**
 * Renders the book section.
 * @returns {React.ReactNode} The rendered book section.
 */
const BookSection = () => {
  const { fullPaper } = useSearch();

  return (
    <div className="container urantia-book-real-format" style={{ maxWidth: '39em' }}>
      {fullPaper?.map((paragraph, index) => {
        const ElementType = getParagraphElement(paragraph);
        
        return (
          <ElementType
            key={`${index}-${paragraph.psp}`}
            id={formatParagraphId(paragraph.psp)}
            className={getParagraphTextClass(paragraph)}
            dangerouslySetInnerHTML={{
              __html: formatParagraphText(paragraph)
            }}
          />
        );
      })}
    </div>
  );
};

export default BookSection; 